import { TypedDocumentNode, gql } from '@apollo/client';
import {
  faArrowUpRight,
  faDollarSign,
  faLink,
} from '@fortawesome/pro-solid-svg-icons';
import { CSSProperties, ReactNode, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Currency, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { IconDefinition } from '@sorare/core/src/atoms/icons';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import {
  BodyL,
  BodyM,
  BodyS,
  HeadlineL,
  LabelM,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import { Avatar } from '@sorare/core/src/components/user/Avatar';
import { ClubShield } from '@sorare/core/src/components/user/ClubShield';
import { UserName } from '@sorare/core/src/components/user/UserName';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion';
import { useInfiniteScroll } from '@sorare/core/src/hooks/useInfiniteScroll';
import { zeroMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { monetaryAmountFragment } from '@sorare/core/src/lib/monetaryAmount';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import {
  ReferralSpecialEvents_currentUser,
  referralLeaderboardQuery,
  referralLeaderboardQueryVariables,
} from './__generated__/index.graphql';

const Root = styled(ButtonBase)`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: var(--unit);
  border-radius: var(--double-and-a-half-unit);
  padding: var(--unit);
  background:
    linear-gradient(transparent, rgba(var(--c-rgb-black), 0.8)),
    var(--bg) center;
  background-size: cover;
  text-align: left;
  @media (${laptopAndAbove}) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`;
const DialogRoot = styled(Vertical).attrs({ gap: 0 })`
  height: 100%;
  background:
    linear-gradient(transparent, rgba(var(--c-rgb-black), 0.7) 50%),
    var(--bg) center;
  background-size: 100%;
  background-position: top;
  background-repeat: no-repeat;
`;
const DialogContent = styled.div`
  padding: var(--triple-unit);
`;

const BlurBackground = styled.div`
  background: rgba(var(--c-rgb-black), 0.7);
  backdrop-filter: blur(10px);
  --bar-bg-color-custom: 'transparent';
`;
const DialogHeader = styled.header`
  isolation: isolate;
  position: relative;
  padding: var(--triple-unit);
  text-align: center;
  margin-top: auto;
`;
const Logo = styled.img`
  border-radius: var(--intermediate-unit);
`;

const CloseButtonWrapper = styled.div`
  position: absolute;
  right: var(--unit);
  top: var(--unit);
`;

const Line = ({
  icon,
  title,
  description,
}: {
  icon: IconDefinition;
  title: ReactNode;
  description: ReactNode;
}) => {
  return (
    <Horizontal gap={3}>
      <IconButton icon={icon} color="quaternary" small asSpan />
      <div>
        <BodyS as="p">{title}</BodyS>
        <BodyS as="p" color="var(--c-nd-600)">
          {description}
        </BodyS>
      </div>
    </Horizontal>
  );
};

const REFERRAL_LEADERBOARD_QUERY = gql`
  query referralLeaderboardQuery(
    $referralSpecialEventId: String!
    $after: String
    $sport: Sport!
  ) {
    referralLeaderboard(referralSpecialEventId: $referralSpecialEventId) {
      id
      rankings(first: 10, after: $after) {
        nodes {
          id
          score
          ranking
          user {
            slug
            id
            userSportProfile: anyUserSportProfile(sport: $sport) {
              id
              clubName
              clubBadge {
                id
                pictureUrl
              }
            }
            ...Avatar_user
            ...UserName_publicUserInfoInterface
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
  ${Avatar.fragments.user}
  ${UserName.fragments.user}
` as TypedDocumentNode<
  referralLeaderboardQuery,
  referralLeaderboardQueryVariables
>;

const Leaderboard = ({ id, sport }: { id: string; sport: Sport }) => {
  const { data, loading, loadMore } = usePaginatedQuery(
    REFERRAL_LEADERBOARD_QUERY,
    {
      variables: {
        referralSpecialEventId: id,
        sport,
      },
      connections: ['ReferralLeaderboardRankingConnection'],
    }
  );

  const { InfiniteScrollLoader } = useInfiniteScroll(
    () => {
      loadMore(false, {
        after: data?.referralLeaderboard.rankings.pageInfo.endCursor,
      });
    },
    data?.referralLeaderboard?.rankings.pageInfo.hasNextPage || false,
    loading
  );
  return (
    <Vertical gap={2}>
      {data?.referralLeaderboard?.rankings.nodes.map(
        ({ id: rankingId, score, user }) => (
          <SBHorizontal key={rankingId}>
            <Horizontal gap={1.5}>
              <Avatar size={5} rounded user={user} />
              <Vertical gap={0}>
                <BodyM>
                  <UserName user={user} />
                </BodyM>
                <Horizontal>
                  <ClubShield
                    size={1}
                    shieldUrl={user.userSportProfile?.clubBadge?.pictureUrl}
                  />
                  <LabelM color="var(--c-nd-600)">
                    {user.userSportProfile?.clubName}
                  </LabelM>
                </Horizontal>
              </Vertical>
            </Horizontal>
            <BodyL>{score}</BodyL>
          </SBHorizontal>
        )
      )}
      <InfiniteScrollLoader />
    </Vertical>
  );
};

type Props = {
  currentUser: ReferralSpecialEvents_currentUser;
  sport: Sport;
};

export const ReferralSpecialEvents = ({ currentUser, sport }: Props) => {
  const [open, setOpen] = useState(false);
  const event = currentUser.referralSpecialEvents?.[0];
  const [referralTab, setReferralTab] = useState<'details' | 'leaderboard'>(
    'details'
  );

  const { main: amount } = useAmountWithConversion({
    monetaryAmount: event?.config?.maxDiscount || zeroMonetaryAmount,
    primaryCurrency: Currency.FIAT,
    hideFractionDigits: true,
  });

  if (!event) {
    return null;
  }

  return (
    <>
      <Root
        style={{ '--bg': `url(${event.tile.backgroundUrl})` } as CSSProperties}
        onClick={() => setOpen(true)}
      >
        <Horizontal>
          <Logo src={event.tile.iconUrl} alt="" width="48" height="48" />
          <Vertical gap={0}>
            <LabelS as="p" color="var(--c-nd-600)" uppercase bold>
              <FormattedMessage
                id="ReferralSpecialEvents.specialEvent"
                defaultMessage="Special event"
              />
            </LabelS>
            <BodyS bold as="h2">
              {event.tile.title}
            </BodyS>
            <LabelS as="p" color="var(--c-nd-600)">
              {event.tile.description}
            </LabelS>
          </Vertical>
        </Horizontal>
        <Button size="small" color="quaternary" asSpan>
          <FormattedMessage
            id="ReferralSpecialEvents.learnMore"
            defaultMessage="Invite friends"
          />
        </Button>
      </Root>
      <Dialog
        open={open}
        maxWidth="xs"
        fullWidth
        hideHeader
        onClose={() => setOpen(false)}
      >
        {({ CloseButton }) => (
          <DialogRoot
            style={
              {
                '--bg': `url(${
                  event.tile.dialogBackgroundUrl || event.tile.backgroundUrl
                })`,
              } as CSSProperties
            }
          >
            <DialogHeader>
              <Vertical center>
                <Logo src={event.tile.iconUrl} alt="" width="48" height="48" />
                <LabelS as="p" color="var(--c-nd-600)" uppercase bold>
                  <FormattedMessage
                    id="ReferralSpecialEvents.specialEvent"
                    defaultMessage="Special event"
                  />
                </LabelS>
                <div>
                  <HeadlineL as="h2">{event.tile.title}</HeadlineL>
                  <BodyS as="p" color="var(--c-nd-600)">
                    {event.tile.description}
                  </BodyS>
                </div>
              </Vertical>
            </DialogHeader>
            <CloseButtonWrapper>
              <CloseButton onClose={() => setOpen(false)} />
            </CloseButtonWrapper>
            <BlurBackground>
              <FullWidthContainer.NoPadding>
                <TabBar
                  value={referralTab}
                  variant="flat"
                  fullWidth
                  forceMobileLayout
                >
                  <Tab
                    value="details"
                    onClick={() => setReferralTab('details')}
                  >
                    <FormattedMessage
                      id="ReferralSpecialEvents.tab.details"
                      defaultMessage="Details"
                    />
                  </Tab>
                  <Tab
                    value="leaderboard"
                    onClick={() => setReferralTab('leaderboard')}
                  >
                    <FormattedMessage
                      id="ReferralSpecialEvents.tab.leaderboard"
                      defaultMessage="Leaderboard"
                    />
                  </Tab>
                </TabBar>
              </FullWidthContainer.NoPadding>
              <DialogContent>
                {referralTab === 'details' && (
                  <Vertical>
                    <Line
                      icon={faLink}
                      title={
                        <FormattedMessage
                          id="ReferralSpecialEvents.Dialog.share.title"
                          defaultMessage="Invite friend by sharing your link"
                        />
                      }
                      description={
                        <FormattedMessage
                          id="ReferralSpecialEvents.dialog.share.desc"
                          defaultMessage="They sign up with your shared link of with your referral code and verify their phone number."
                        />
                      }
                    />
                    <Line
                      icon={faArrowUpRight}
                      title={
                        <FormattedMessage
                          id="ReferralSpecialEvents.Dialog.challenge.title"
                          defaultMessage="They complete their challenges"
                        />
                      }
                      description={
                        <Vertical>
                          {event.challenges.map(
                            ({
                              id,
                              description,
                              stats: { completed, failed, inProgress },
                            }) => {
                              const total = completed + failed + inProgress;
                              return (
                                <SBHorizontal key={id}>
                                  {description}
                                  {total ? (
                                    <BodyS>
                                      {completed}&nbsp;/&nbsp;
                                      {total}
                                    </BodyS>
                                  ) : undefined}
                                </SBHorizontal>
                              );
                            }
                          )}
                        </Vertical>
                      }
                    />
                    {!!event?.config?.maxDiscount && (
                      <Line
                        icon={faDollarSign}
                        title={
                          <FormattedMessage
                            id="ReferralSpecialEvents.Dialog.youEarn.title"
                            defaultMessage="You earn {amount}"
                            values={{
                              amount,
                            }}
                          />
                        }
                        description={
                          <FormattedMessage
                            id="ReferralSpecialEvents.dialog.earn.desc"
                            defaultMessage="The more your friend complete their tasks, the more you will earn rewards"
                          />
                        }
                      />
                    )}
                  </Vertical>
                )}
                {referralTab === 'leaderboard' && event && (
                  <Leaderboard id={event.id} sport={sport} />
                )}
              </DialogContent>
            </BlurBackground>
          </DialogRoot>
        )}
      </Dialog>
    </>
  );
};

ReferralSpecialEvents.fragments = {
  currentUser: gql`
    fragment ReferralSpecialEvents_currentUser on CurrentUser {
      slug
      referralSpecialEvents(sport: $sport) {
        id
        tile {
          id
          description
          title
          iconUrl
          dialogBackgroundUrl
          backgroundUrl
        }
        config {
          maxDiscount {
            ...MonetaryAmountFragment_monetaryAmount
          }
        }
        challenges {
          id
          description
          stats {
            completed
            failed
            inProgress
          }
        }
      }
    }
    ${monetaryAmountFragment}
  ` as TypedDocumentNode<ReferralSpecialEvents_currentUser>,
};
